import {
  rbcAccountsApi,
  type NotAvailable,
  RewardsAccountResponseEnum,
  type Available,
} from "@b2bportal/rbc-accounts-api";
import { axiosInstance } from "@hopper-b2b/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "@/store";
import { postFeatureEventToNativeBridge } from "@/utils/nativeBridge";

const UnknownErrorStatus = "98";
let aCallIsInFlight = false;

export type AvailablePointsResponse = {
  RewardsAccountResponse: "Available";
} & Available;

type FetchPointsBalanceArgs = {
  /**
   * If true, notifies the native app to refresh points display.
   * Should only be true after booking completion, not on regular fetches.
   */
  notifyNativeApp?: boolean;
};

export const fetchPointsBalance = createAsyncThunk<
  AvailablePointsResponse,
  FetchPointsBalanceArgs | void,
  {
    state: RootState;
    rejectValue: NotAvailable | AvailablePointsResponse;
  }
>("account/getPointsBalance", async (args, thunkAPI) => {
  if (aCallIsInFlight) {
    thunkAPI.abort("fetchPointsBalance is in flight");
    return;
  }

  const shouldNotifyNativeApp = args?.notifyNativeApp ?? false;

  try {
    aCallIsInFlight = true;
    const response = await rbcAccountsApi(axiosInstance)
      .apiV0AccountsPointsBalanceGet()
      .then((res) => res.data);

    if (
      response.RewardsAccountResponse ===
      RewardsAccountResponseEnum.NotAvailable
    ) {
      return thunkAPI.rejectWithValue(response as any);
    }

    // Only notify mobile app when explicitly requested (e.g., after booking completion)
    if (shouldNotifyNativeApp) {
      postFeatureEventToNativeBridge("travel", "update-points");
    }

    return response;
  } catch (error) {
    console.error("fetchPointsBalance threw", { error });
    return thunkAPI.rejectWithValue({
      RewardsAccountResponse: RewardsAccountResponseEnum.NotAvailable,
      reason: UnknownErrorStatus,
    });
  } finally {
    aCallIsInFlight = false;
  }
});
